// CLIENT PORTAL §2 — WHICH OF FOUR THINGS A REPORT PAGE RENDERS (2026-09-04).
//
// ⚠️⚠️ "CUSTOM RANGE…" WAS A PERMANENT DEADLOCK, AND HAD BEEN SINCE THE PERIOD
// SELECTOR SHIPPED. `useReportState` returns `params === null` for a custom
// preset with no dates yet — correct, and `enabled: state.params !== null` uses
// it correctly. But all three pages did `if (state.params === null) return
// <Loading />`, and `Loading` replaces the whole body with a spinner — and the
// `PeriodControl` that would supply the dates is INSIDE that body. The control
// the page was waiting for is what the spinner removed. Nothing throws, which is
// why the console was clean; it was not hung, it was waiting for input it had
// just made unreachable.
//
// ⚠️⚠️ THE MECHANISM: ONE VALUE, TWO MEANINGS. `params === null` means "do not
// fetch yet" to the query layer, where it is right, and was read as "there is
// nothing to show" at the render layer, where it is catastrophic. A SENTINEL
// THAT MEANS "WAIT" DOWNSTREAM MUST NEVER BE READ AS "NOTHING" UPSTREAM.
//
// ⚠️ SO THE DECISION IS PURE AND LIVES HERE, not repeated in three pages. It was
// three copies of a four-line guard trio agreeing by coincidence — the same
// shape as round 6's three `'lastYear'` literals, and one of the three copies is
// exactly where a future edit would diverge.

/** What a report page shows. */
export type ReportView =
  /**
   * ⚠️⚠️ WAITING FOR THE READER, NOT FOR DATA — and this value existing is the
   * whole fix. A custom range with one end picked (or none) cannot be fetched,
   * so the page must render the CONTROLS and say what it needs. Rendering a
   * spinner here is the deadlock: it removes the only control that can resolve
   * it.
   */
  | 'pick-dates'
  /** A genuine cold start: the range is complete and the first fetch is in flight. */
  | 'loading'
  | 'error'
  /** The report — the current one, or the previous one while a new range is picked. */
  | 'report'
  /**
   * ⚠️⚠️ THE REPORT IS ON SCREEN AND A NEW ONE IS IN FLIGHT (§8, 2026-09-04).
   *
   * The state `placeholderData` created and nothing filled. Round 7 §2 added it
   * so the picker survives a half-picked custom range — which means `query.data`
   * is never empty on a key change, so react-query's `isLoading` is false and
   * this resolver had NO state between "loading" and "report". Scott found it
   * as 5—10 seconds of a page that does not change and then silently does.
   *
   * ⚠️ IT RENDERS THE REPORT, not a spinner in place of it. Content stays and
   * the chrome stays interactive, because a reader must be able to change their
   * mind mid-fetch — which is also what keeps §2's deadlock fixed.
   */
  | 'refreshing';

export interface ReportViewInput {
  /** `state.params !== null` — is there a fetchable window? */
  paramsReady: boolean;
  /**
   * Is there a report to draw?
   *
   * ⚠️ TRUE FOR PLACEHOLDER DATA TOO. `placeholderData: (prev) => prev` keeps
   * the previous report while a custom range is half-picked, which is what keeps
   * the chrome — and therefore the picker — mounted on the warm path.
   */
  hasReport: boolean;
  isLoading: boolean;
  hasError: boolean;
  /**
   * A fetch is in flight AND what is on screen is not its answer.
   *
   * ⚠️⚠️ THE NARROW PAIR — `isFetching && isPlaceholderData`, NOT `isFetching`
   * alone. A background refetch of the SAME key (a window refocus, a
   * `refetchOnMount`) is fetching too, and flashing a pending cue over figures
   * that are already correct trains a reader to ignore it. `isPlaceholderData`
   * is precisely "what you are looking at came from the PREVIOUS key", which is
   * the only case where the screen and the controls disagree.
   */
  isRefreshing: boolean;
}

/**
 * ⚠️ THE ORDER IS THE RULE, and it maps one-for-one onto the guard trio it
 * replaces, with exactly one change: `paramsReady === false` no longer means
 * "loading" — it means "pick-dates", and only when there is nothing to show.
 *
 * ⚠️ AN ERROR IS STILL NEVER SWALLOWED. With `placeholderData` a FAILED refetch
 * arrives as stale data plus an error, and the previous code showed the error
 * (`query.error || !query.data`). Keeping that means a client never reads
 * yesterday's figures under today's dates because a fetch quietly failed.
 *
 * ⚠️⚠️ AND `refreshing` COMES LAST, AFTER THE ERROR CHECK. A refetch that FAILED
 * is still `isFetching: false` by then, but the ordering is what states the
 * rule: a pending cue must never be shown in place of a failure, or a client
 * waits for something that already went wrong.
 */
export function resolveReportView(input: ReportViewInput): ReportView {
  // ⚠️ FIRST, and only when there is nothing else to draw: on a WARM change of
  // range the previous report stays and the reader keeps their bearings.
  if (!input.paramsReady && !input.hasReport) return 'pick-dates';
  if (input.isLoading) return 'loading';
  if (input.hasError || !input.hasReport) return 'error';
  if (input.isRefreshing) return 'refreshing';
  return 'report';
}

/**
 * Does this view put the report on screen?
 *
 * ⚠️⚠️ ONE NAMED CONDITION, BECAUSE `refreshing` IS THE KIND OF ADDITION THAT
 * BREAKS AN `=== 'report'` CHECK SILENTLY. Two views now render content, so any
 * caller testing for the single string would stop drawing the report the moment
 * a fetch started — which is the regression this whole section is fixing, in a
 * new coat.
 */
export function reportIsVisible(view: ReportView): boolean {
  return view === 'report' || view === 'refreshing';
}

/**
 * What the page says while it waits for the reader.
 *
 * ⚠️ IT NAMES WHAT IS MISSING rather than apologising. A client who has clicked
 * "Custom range…" and picked one date needs to know a second click finishes it;
 * "loading" told them to wait for something that was never coming.
 */
export function pickDatesNote(hasStart: boolean): string {
  return hasStart
    ? 'Pick the end of your date range to see the report.'
    : 'Pick a start and end date to see the report.';
}
